// Management → Activities — all-customer activity oversight for managers.
// One master screen: List + Calendar views over every planned CRM activity,
// sortable by tier & spend, filterable by status / type / account manager,
// with an overdue lens. Read surface onto /api/management/activities.
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Layout from "../../components/Layout";
import Person360Overlay from "../../components/Person360Overlay";
import {
  Gauge, Search, RefreshCw, Loader2, List as ListIcon, CalendarDays,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle,
  CheckCircle2, XCircle, Clock, PoundSterling, Trash2, X, Sparkles,
} from "lucide-react";
import { metaFor, toneCls, ACTIVITY_TYPES } from "../../components/activity/activityMeta";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const money = (n) =>
  n == null || n === 0 ? "£0" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const fmtDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return String(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};
const tierCls = (t) => {
  const k = String(t || "").toLowerCase();
  if (k.includes("black")) return "bg-slate-900 text-slate-100 border-slate-500/40";
  if (k.includes("plat")) return "bg-slate-200/10 text-slate-100 border-slate-300/40";
  if (k.includes("gold")) return "bg-amber-500/15 text-amber-300 border-amber-500/40";
  if (k.includes("silver")) return "bg-slate-400/15 text-slate-200 border-slate-400/40";
  if (k.includes("bronze")) return "bg-orange-800/20 text-orange-300 border-orange-700/40";
  return "border-white/10 text-slate-500";
};

const STATUS_META = {
  open:      { label: "Open",      cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", icon: Clock },
  overdue:   { label: "Overdue",   cls: "bg-rose-500/15 text-rose-300 border-rose-500/40", icon: AlertTriangle },
  done:      { label: "Done",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "bg-slate-500/15 text-slate-400 border-slate-500/40", icon: XCircle },
};
const statusKey = (r) => (r.overdue ? "overdue" : r.status);

function StatusPill({ r }) {
  const m = STATUS_META[statusKey(r)] || STATUS_META.open;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${m.cls}`} data-testid={`mgmt-act-status-${r.id}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function TypePill({ type }) {
  const m = metaFor(type);
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${toneCls(m.tone)}`}>
      <Icon className="w-3 h-3" /> {type}
    </span>
  );
}

function SortTh({ label, k, sort, dir, onSort, align = "left" }) {
  const active = sort === k;
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`py-2 px-3 select-none ${align === "right" ? "text-right" : "text-left"}`}>
      <button onClick={() => onSort(k)} data-testid={`mgmt-act-sort-${k}`}
        className={`inline-flex items-center gap-1 ${active ? "text-orange-300" : "text-slate-400 hover:text-white"}`}>
        {align === "right" && <Icon className="w-3 h-3" />}{label}{align !== "right" && <Icon className="w-3 h-3" />}
      </button>
    </th>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const iso = (d) => d.toISOString().slice(0, 10);

export default function ManagementActivities() {
  const [view, setView] = useState("list");
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ status: {}, types: {}, ams: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState("all");  // all|open|overdue|done|cancelled
  const [type, setType] = useState("");
  const [am, setAm] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("tier");
  const [dir, setDir] = useState("asc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // calendar
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [calRows, setCalRows] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [selDay, setSelDay] = useState(null);

  const [person360Id, setPerson360Id] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [cleanupLog, setCleanupLog] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const token = () => localStorage.getItem("talmia_token");
  const authCfg = useMemo(() => ({ headers: { Authorization: `Bearer ${token()}` }, withCredentials: true }), []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, direction: dir, page, page_size: PAGE_SIZE };
      if (status !== "all") params.status = status;
      if (type) params.activity_type = type;
      if (am) params.am = am;
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API}/management/activities`, { ...authCfg, params });
      setRows(data.rows || []);
      setFacets(data.facets || { status: {}, types: {}, ams: [] });
      setTotal(data.total || 0);
    } catch (e) {
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, [status, type, am, q, sort, dir, page, authCfg]);

  useEffect(() => { if (view === "list") fetchList(); }, [view, fetchList]);

  const fetchCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const start = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
      const end = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0);
      const params = { date_from: iso(start), date_to: iso(end), page_size: 500, sort: "due", direction: "asc" };
      if (status !== "all") params.status = status;
      if (type) params.activity_type = type;
      if (am) params.am = am;
      const { data } = await axios.get(`${API}/management/activities`, { ...authCfg, params });
      setCalRows(data.rows || []);
      setFacets((f) => data.facets || f);
    } catch (e) { setCalRows([]); }
    finally { setCalLoading(false); }
  }, [calMonth, status, type, am, authCfg]);

  useEffect(() => { if (view === "calendar") fetchCalendar(); }, [view, fetchCalendar]);

  const fetchLog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/management/activities/cleanup-log`, { ...authCfg, params: { days: 30 } });
      setCleanupLog(data);
    } catch (e) { setCleanupLog({ rows: [], total: 0, by_am: [] }); }
  }, [authCfg]);
  useEffect(() => { fetchLog(); }, [fetchLog]);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleSelAll = () => setSelected((s) => {
    if (rows.every((r) => s.has(r.id))) { const n = new Set(s); rows.forEach((r) => n.delete(r.id)); return n; }
    const n = new Set(s); rows.forEach((r) => n.add(r.id)); return n;
  });
  const bulkCancel = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/management/activities/bulk-cancel`, { ids: Array.from(selected) }, authCfg);
      setSelected(new Set());
      await fetchList();
    } finally { setBulkBusy(false); }
  };

  const onSort = (k) => {
    if (sort === k) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setDir(k === "tier" || k === "spend" ? "asc" : "asc"); }
    setPage(1);
  };

  const byDay = useMemo(() => {
    const m = {};
    for (const r of calRows) {
      if (!r.due_date) continue;
      (m[r.due_date] = m[r.due_date] || []).push(r);
    }
    return m;
  }, [calRows]);

  const calCells = useMemo(() => {
    const first = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
    const daysIn = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
    let lead = first.getDay() - 1; if (lead < 0) lead = 6; // Monday-first
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(new Date(calMonth.getFullYear(), calMonth.getMonth(), d));
    return cells;
  }, [calMonth]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sc = facets.status || {};

  const StatusChip = ({ k, label }) => {
    const sTotal = (sc.open || 0) + (sc.done || 0) + (sc.cancelled || 0);
    const n = k === "all" ? sTotal : sc[k];
    const active = status === k;
    const m = STATUS_META[k];
    return (
      <button data-testid={`mgmt-act-filter-status-${k}`}
        onClick={() => { setStatus(k); setPage(1); }}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active ? (m ? m.cls : "bg-orange-500/20 text-orange-200 border-orange-500/50") : "border-white/10 text-slate-400 hover:text-white"}`}>
        {label}{n != null && <span className="opacity-70 font-mono">{n}</span>}
      </button>
    );
  };

  return (
    <Layout>
      <div className="max-w-[1500px] mx-auto px-4 py-5" data-testid="mgmt-activities-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Gauge className="w-5 h-5 text-amber-300" />
              <h1 className="text-xl font-display font-semibold">Activities</h1>
            </div>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Every planned activity across all customers — who's chasing what, by tier & spend.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              <button data-testid="mgmt-act-view-list" onClick={() => setView("list")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] ${view === "list" ? "bg-orange-500/20 text-orange-200" : "text-slate-400 hover:text-white"}`}>
                <ListIcon className="w-3.5 h-3.5" /> List
              </button>
              <button data-testid="mgmt-act-view-calendar" onClick={() => setView("calendar")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] ${view === "calendar" ? "bg-orange-500/20 text-orange-200" : "text-slate-400 hover:text-white"}`}>
                <CalendarDays className="w-3.5 h-3.5" /> Calendar
              </button>
            </div>
            <button data-testid="mgmt-act-refresh" onClick={() => (view === "list" ? fetchList() : fetchCalendar())}
              className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/25">
              <RefreshCw className={`w-4 h-4 ${(loading || calLoading) ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex items-center gap-1.5">
            <StatusChip k="all" label="All" />
            <StatusChip k="open" label="Open" />
            <StatusChip k="overdue" label="Overdue" />
            <StatusChip k="done" label="Done" />
            <StatusChip k="cancelled" label="Cancelled" />
          </div>
          <div className="h-5 w-px bg-white/10 mx-1" />
          <select data-testid="mgmt-act-filter-type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}
            className="px-2.5 py-1.5 rounded-lg bg-slate-900/70 border border-white/10 text-[12px] text-white outline-none focus:border-orange-400/60">
            <option value="">All types</option>
            {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="mgmt-act-filter-am" value={am} onChange={(e) => { setAm(e.target.value); setPage(1); }}
            className="px-2.5 py-1.5 rounded-lg bg-slate-900/70 border border-white/10 text-[12px] text-white outline-none focus:border-orange-400/60">
            <option value="">All account managers</option>
            {(facets.ams || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="relative flex-1 min-w-[180px] max-w-[320px]">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input data-testid="mgmt-act-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search subject or customer…"
              className="w-full pl-8 pr-2.5 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12px] text-white placeholder:text-slate-500 outline-none focus:border-orange-400/60" />
          </div>
        </div>

        {/* Bulk actions + cleanup log toggle */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {selected.size > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-rose-500/40 bg-rose-500/10" data-testid="mgmt-act-bulk-bar">
              <span className="text-[12px] text-rose-200">{selected.size} selected</span>
              <button onClick={bulkCancel} disabled={bulkBusy} data-testid="mgmt-act-bulk-cancel"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium border border-rose-500/50 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 disabled:opacity-50">
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Cancel selected
              </button>
              <button onClick={() => setSelected(new Set())} data-testid="mgmt-act-bulk-clear" className="text-[11px] text-slate-400 hover:text-white">Clear</button>
            </div>
          )}
          <button onClick={() => setShowLog((v) => !v)} data-testid="mgmt-act-log-toggle"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] border ml-auto transition-colors ${showLog ? "border-amber-500/50 bg-amber-500/15 text-amber-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
            <Trash2 className="w-3.5 h-3.5" /> Auto-cleanup log{cleanupLog?.total ? ` (${cleanupLog.total})` : ""}
          </button>
        </div>

        {showLog && cleanupLog && (
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-3 mb-3" data-testid="mgmt-act-cleanup-log">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span className="text-[12px] font-semibold text-amber-100">Auto-cancelled stale activities</span>
              <span className="text-[11px] text-slate-500 font-mono">· last {cleanupLog.window_days}d · overdue &gt;14d</span>
            </div>
            {cleanupLog.by_am.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="mgmt-act-cleanup-byam">
                {cleanupLog.by_am.map((a) => (
                  <span key={a.am} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] border border-white/10 bg-white/[0.03] text-slate-300">
                    {a.am} <span className="font-mono text-amber-300">{a.count}</span>
                  </span>
                ))}
              </div>
            )}
            {cleanupLog.rows.length === 0 ? (
              <div className="text-[12px] text-slate-500 italic py-2">Nothing auto-cancelled in this window.</div>
            ) : (
              <div className="max-h-[220px] overflow-y-auto space-y-1">
                {cleanupLog.rows.slice(0, 50).map((r) => (
                  <div key={r._id} className="flex items-center gap-2 text-[12px] px-2 py-1 rounded bg-black/20 border border-white/[0.05]">
                    <span className="text-slate-300 truncate max-w-[180px]">{r.customer}</span>
                    {r.tier && <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${tierCls(r.tier)}`}>{r.tier}</span>}
                    <span className="text-slate-400 truncate flex-1">{r.subject}</span>
                    <span className="text-rose-300/80 font-mono text-[10px] shrink-0">{r.days_overdue != null ? `${r.days_overdue}d over` : ""}</span>
                    <span className="text-slate-500 shrink-0">{r.am || "Unassigned"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "list" && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="mgmt-act-table">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-white/10 bg-white/[0.02]">
                  <tr>
                    <th className="py-2 px-3 w-8">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelAll} data-testid="mgmt-act-select-all"
                        className="w-3.5 h-3.5 rounded border-white/20 bg-white/5 accent-orange-500" />
                    </th>
                    <SortTh label="Customer" k="customer" sort={sort} dir={dir} onSort={onSort} />
                    <SortTh label="Tier" k="tier" sort={sort} dir={dir} onSort={onSort} />
                    <SortTh label="Spend" k="spend" sort={sort} dir={dir} onSort={onSort} align="right" />
                    <th className="py-2 px-3 text-left">Account Manager</th>
                    <th className="py-2 px-3 text-left">Type</th>
                    <th className="py-2 px-3 text-left">Subject</th>
                    <SortTh label="Due" k="due" sort={sort} dir={dir} onSort={onSort} />
                    <th className="py-2 px-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={9} className="py-10 text-center text-slate-500" data-testid="mgmt-act-empty">No activities match these filters.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.id} data-testid={`mgmt-act-row-${r.id}`}
                      onClick={() => r.person_id && setPerson360Id(r.person_id)}
                      className="border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer">
                      <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} data-testid={`mgmt-act-select-${r.id}`}
                          className="w-3.5 h-3.5 rounded border-white/20 bg-white/5 accent-orange-500" />
                      </td>
                      <td className="py-2 px-3 text-slate-200 font-medium">{r.customer}</td>
                      <td className="py-2 px-3">
                        {r.tier ? <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${tierCls(r.tier)}`}>{r.tier}</span> : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-emerald-300/90">{money(r.ltv)}</td>
                      <td className="py-2 px-3 text-slate-300">{r.am || <span className="text-slate-600 italic">Unassigned</span>}</td>
                      <td className="py-2 px-3"><TypePill type={r.activity_type} /></td>
                      <td className="py-2 px-3 text-slate-300 max-w-[280px] truncate" title={r.subject}>{r.subject}</td>
                      <td className="py-2 px-3 font-mono text-[12px] text-slate-400">{fmtDay(r.due_date)}</td>
                      <td className="py-2 px-3"><StatusPill r={r} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* pagination */}
            <div className="flex items-center justify-between px-3 py-2 border-t border-white/10 text-[12px] text-slate-400">
              <span>{total} activit{total === 1 ? "y" : "ies"}</span>
              <div className="flex items-center gap-2">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="mgmt-act-prev"
                  className="p-1 rounded border border-white/10 disabled:opacity-30 hover:border-white/25"><ChevronLeft className="w-4 h-4" /></button>
                <span className="font-mono">{page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="mgmt-act-next"
                  className="p-1 rounded border border-white/10 disabled:opacity-30 hover:border-white/25"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        )}

        {view === "calendar" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3" data-testid="mgmt-act-calendar">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="flex items-center justify-between mb-3">
                <button data-testid="mgmt-act-cal-prev" onClick={() => setCalMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                  className="p-1.5 rounded border border-white/10 text-slate-400 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
                <div className="font-display font-semibold text-slate-200">{MONTHS[calMonth.getMonth()]} {calMonth.getFullYear()}</div>
                <div className="flex items-center gap-1.5">
                  <button data-testid="mgmt-act-cal-today" onClick={() => { const d = new Date(); d.setDate(1); setCalMonth(d); }}
                    className="px-2 py-1 rounded border border-white/10 text-[11px] text-slate-400 hover:text-white">Today</button>
                  <button data-testid="mgmt-act-cal-next" onClick={() => setCalMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                    className="p-1.5 rounded border border-white/10 text-slate-400 hover:text-white"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-center py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calCells.map((cell, i) => {
                  if (!cell) return <div key={`b${i}`} className="min-h-[76px] rounded-lg" />;
                  const key = iso(cell);
                  const list = byDay[key] || [];
                  const isToday = key === iso(new Date());
                  const isSel = selDay === key;
                  return (
                    <button key={key} data-testid={`mgmt-act-cal-day-${key}`} onClick={() => setSelDay(key)}
                      className={`min-h-[76px] rounded-lg border p-1 text-left align-top transition-colors ${isSel ? "border-orange-400/60 bg-orange-500/[0.07]" : "border-white/[0.06] bg-white/[0.015] hover:border-white/20"}`}>
                      <div className={`text-[11px] font-mono mb-0.5 ${isToday ? "text-orange-300 font-bold" : "text-slate-400"}`}>{cell.getDate()}</div>
                      <div className="space-y-0.5">
                        {list.slice(0, 3).map((r) => {
                          const m = metaFor(r.activity_type);
                          return (
                            <div key={r.id} className={`truncate text-[9.5px] px-1 py-0.5 rounded border ${r.overdue ? STATUS_META.overdue.cls : toneCls(m.tone)}`} title={`${r.customer}: ${r.subject}`}>
                              {r.subject}
                            </div>
                          );
                        })}
                        {list.length > 3 && <div className="text-[9px] text-slate-500 px-1">+{list.length - 3} more</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
              {calLoading && <div className="text-center text-slate-500 py-2"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
            </div>
            {/* Day panel */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="mgmt-act-cal-panel">
              <div className="text-[11px] uppercase tracking-wider text-amber-300 font-mono mb-2">
                {selDay ? fmtDay(selDay) : "Select a day"}
              </div>
              {selDay ? (
                (byDay[selDay] || []).length === 0 ? (
                  <div className="text-slate-500 text-[12px] italic py-4 text-center">No activities due this day.</div>
                ) : (
                  <div className="space-y-2">
                    {(byDay[selDay] || []).map((r) => (
                      <div key={r.id} data-testid={`mgmt-act-cal-item-${r.id}`} onClick={() => r.person_id && setPerson360Id(r.person_id)}
                        className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2 cursor-pointer hover:border-white/20">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-[12px] text-slate-200 font-medium truncate">{r.customer}</span>
                          <StatusPill r={r} />
                        </div>
                        <div className="flex items-center gap-1.5 mb-1"><TypePill type={r.activity_type} />
                          {r.tier && <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${tierCls(r.tier)}`}>{r.tier}</span>}
                        </div>
                        <div className="text-[12px] text-slate-300 truncate">{r.subject}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex items-center gap-2">
                          <span>{r.am || "Unassigned"}</span>
                          <span className="inline-flex items-center gap-0.5"><PoundSterling className="w-3 h-3" />{money(r.ltv).replace("£", "")}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-slate-500 text-[12px] italic py-4 text-center">Click a day to see its activities.</div>
              )}
            </div>
          </div>
        )}
      </div>
      <Person360Overlay personId={person360Id} onClose={() => setPerson360Id(null)} />
    </Layout>
  );
}
